import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { onValue } from 'firebase/database';
import { dbRef } from 'utils/dbRef';
import { useSiteInfo } from 'hooks/useSiteInfo';
import { storageGet } from 'utils/safeStorage';

// Fallback content structure (used only if Firebase fails)
export const fallbackContent = {
  seo: {
    title: 'Birreria El Semaforo | Home',
    description:
      'Bringing bold, authentic Mexican flavors to Nampa! Known for our rich birria, unique Birria Tamales, and refreshing Agua Frescas.',
    canonical: '/',
    ogImageURL: '',
  },
  hero: {
    title: 'Mi Casa, Tu Craving',
    subtitle: 'Classic recipes. Bold flavors. A new vibe in every bite.',
    eyebrow: 'Hecho con pasión',
    heroImage: 'Calidad y Tradición!',
    imageAlt: 'Hero image',
    primaryButtonText: 'Order Online',
    secondaryButtonText: 'View Location',
  },
  about: {
    title: 'Family, Flavor, Forever',
    info: 'Bringing bold, authentic Mexican flavors to Nampa: known for our rich birria, unique Birria Tamales, and refreshing Agua Frescas.',
    backgroundImage: '',
    galleryImages: [],
  },
  logo: {
    secondaryLogo: '',
    whiteLogo: '',
  },
  location: {
    title: 'Stop By El Semáforo',
    description:
      'Join us for an unforgettable dining experience! Bring your family and friends, and let us treat you to the flavors of Mexico!',
    addressOne: '5830 E Franklin, Nampa, ID 83651',
    phone: '208-586-3300',
    email: 'birriaelsemaforo@gmail.com',
  },
  hours: [
    { day: 'Monday', time: 'Closed' },
    { day: 'Tuesday - Saturday', time: '11am - 9pm' },
    { day: 'Sunday', time: '11am - 8pm' },
  ],
  menu: {
    backgroundImage: '',
    buttonText: 'Order Now',
    subtitle:
      'Packed with flavor, made with love. Taste the difference in every bite!',
    title: 'Real Food, Real Sabor',
  },
  social: {
    facebook: '',
    instagram: '',
    tiktok: '',
    yelp: '',
  },
  messages: {
    menuButtonFallback: 'Menu',
    contactForm: {
      nameLabel: 'Name',
      namePlaceholder: 'Name',
      emailLabel: 'Email',
      emailPlaceholder: 'Email',
      messageLabel: 'Message',
      messagePlaceholder: 'Message',
      sending: 'Sending...',
      success: 'Message Sent!',
      error: 'Error. Try Again',
      submit: 'Send Message',
    },
    navigation: {
      home: 'Home',
      menu: 'Menu',
      about: 'About',
      location: 'Location',
      login: 'Login',
    },
    locationLabels: {
      restaurantLocation: 'Restaurant Location',
      hours: 'Hours',
      contactInfo: 'Contact Info',
      followUs: 'Follow Us On:',
    },
  },
};

// Create context
const ContentContext = createContext();

// Detect browser language preference
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  // Check if browser language is Spanish
  return browserLang.toLowerCase().startsWith('es') ? 'es' : 'en';
};

// Get initial language from localStorage or browser. This runs during the
// first render, so a browser that blocks storage must not make it throw (#617).
const getInitialLanguage = () => {
  const stored = storageGet('localStorage', 'preferredLanguage');
  if (stored && (stored === 'en' || stored === 'es')) {
    return stored;
  }
  return detectBrowserLanguage();
};

// Main provider component
export const ContentProvider = ({ children }) => {
  const [firebaseContent, setFirebaseContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState(getInitialLanguage());
  const { languageSwitcherEnabled } = useSiteInfo();

  // Force English when language switcher is disabled
  const effectiveLanguage = languageSwitcherEnabled ? language : 'en';

  useEffect(() => {
    // Reference to siteContent in Firebase
    const contentRef = dbRef('siteContent');

    // Subscribe to real-time updates
    const unsubscribe = onValue(
      contentRef,
      (snapshot) => {
        try {
          const data = snapshot.val();
          if (data) {
            setFirebaseContent(data);
            setError(null);
          } else {
            // No data in Firebase, will use fallback
            setFirebaseContent(null);
          }
        } catch (err) {
          Sentry.captureException(err, {
            tags: { component: 'ContentContext', action: 'fetchContent' },
          });
          setError(err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        // Error callback
        Sentry.captureException(err, {
          tags: { component: 'ContentContext', action: 'fetchContent' },
        });
        setError(err);
        setLoading(false);
      },
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Get content for current language
  const getLanguageContent = () => {
    if (firebaseContent && firebaseContent[effectiveLanguage]) {
      return firebaseContent[effectiveLanguage];
    }
    // Fallback to inline content if Firebase fails
    return fallbackContent;
  };

  // Flatten the structure to make destructuring easier
  const content = {
    // Spread language-specific content from Firebase or fallback
    ...getLanguageContent(),
    // Add content management functions
    loading,
    error,
    language: effectiveLanguage,
    setLanguage,
    isUsingFirebase: !!firebaseContent,
  };

  return (
    <ContentContext.Provider value={content}>
      {children}
    </ContentContext.Provider>
  );
};

// Single hook for accessing all content
export const useContent = () => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

// Export default for convenience
export default useContent;
